import { dateProvider } from '@utils';
import { createRef } from 'react';
import { useEffect, useMemo } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { create } from 'zustand';

import { getFirstSlideIndex } from '../pages/Student/Dashboard/components/Widgets/TasksWidget/utils';

import { useIsMobile } from '@/hooks';
import { useMyPlanification } from '@/store';
import { isActivityCompleted } from '@/utils';

interface PlanificationTimelineState {
  selectedDate: string;
  currentSlide: number;
  isLoading: boolean;
  carouselRef: React.RefObject<SwiperType>;
  setSelectedDate: (date: string) => void;
  setCurrentSlide: (slide: number) => void;
  setIsLoading: (loading: boolean) => void;
}

const firstCarouselRef = createRef<SwiperType>();

const usePlanificationTimelineStore = create<PlanificationTimelineState>(set => ({
  selectedDate: '',
  currentSlide: 0,
  isLoading: true,
  carouselRef: firstCarouselRef,
  setSelectedDate: (date: string) => set({ selectedDate: date }),
  setCurrentSlide: (slide: number) => set({ currentSlide: slide }),
  setIsLoading: (loading: boolean) => set({ isLoading: loading })
}));

export const usePlanificationTimeline = () => {
  const isMobile = useIsMobile('sm');
  const { planification, sortedPlanificationActivitiesByDate } = useMyPlanification();

  const {
    selectedDate,
    currentSlide,
    isLoading,
    carouselRef,
    setSelectedDate,
    setCurrentSlide,
    setIsLoading
  } = usePlanificationTimelineStore();

  const planificationDates = useMemo(() => {
    if (!planification) return [];

    const { startDate, endDate } = planification;
    const startOfDay = dateProvider.startOfDay(dateProvider.parse(startDate));
    const endOfDay = dateProvider.startOfDay(dateProvider.parse(endDate));
    if (dateProvider.isAfter(startOfDay, endOfDay)) {
      return [];
    }
    const dayBeforeStart = dateProvider.subtract(startOfDay, 1, 'days');
    return dateProvider
      .eachDayOfInterval(dayBeforeStart, endOfDay)
      .map(date => new Date(date).toISOString().split('T')[0]);
  }, [planification?.startDate, planification?.endDate]);

  useEffect(() => {
    if (!planification || planificationDates.length === 0) {
      setIsLoading(false);
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    const endDateString = new Date(planification.endDate).toISOString().split('T')[0];

    let defaultDate: string;

    if (planificationDates.includes(today)) {
      defaultDate = today;
    } else if (today > endDateString) {
      defaultDate = endDateString;
    } else {
      defaultDate = planificationDates[0];
    }

    setSelectedDate(defaultDate);
    setIsLoading(false);
  }, [planification, planificationDates, setIsLoading]);

  useEffect(() => {
    const swiper = carouselRef.current;
    if (planificationDates.length === 0 || !swiper) return;
    if (swiper.destroyed || !swiper.params || typeof swiper.slideTo !== 'function') return;

    const firstSlideIndex = getFirstSlideIndex(planificationDates, isMobile, selectedDate);
    swiper.slideTo(firstSlideIndex);
    setCurrentSlide(firstSlideIndex);
  }, [planificationDates, isMobile, setCurrentSlide, carouselRef, selectedDate]);

  const handlePrev = () => {
    const swiper = carouselRef.current;
    if (!swiper || swiper.destroyed || typeof swiper.slidePrev !== 'function') return;
    swiper.slidePrev();
    setCurrentSlide(swiper.activeIndex - 1);
  };

  const handleNext = () => {
    const swiper = carouselRef.current;
    if (!swiper || swiper.destroyed || typeof swiper.slideNext !== 'function') return;
    swiper.slideNext();
    setCurrentSlide(swiper.activeIndex + 1);
  };

  const handleDateClick = (date: string) => {
    setSelectedDate(date);
  };

  const completedTasksCountSelectedDate = useMemo(() => {
    const activities = sortedPlanificationActivitiesByDate[selectedDate];
    return (
      activities?.filter(activity =>
        isActivityCompleted(activity.activity?._id, activity.subscription)
      ).length || 0
    );
  }, [sortedPlanificationActivitiesByDate, selectedDate]);

  const totalTasksCountSelectedDate = useMemo(() => {
    return sortedPlanificationActivitiesByDate[selectedDate]?.length || 0;
  }, [sortedPlanificationActivitiesByDate, selectedDate]);

  return {
    selectedDate,
    currentSlide,
    isLoading,
    planificationDates,
    carouselRef,
    handlePrev,
    handleNext,
    handleDateClick,
    setCurrentSlide,
    hasPlanification: !!planification,
    completedTasksCountSelectedDate,
    totalTasksCountSelectedDate,
    sortedPlanificationActivitiesByDate
  };
};
